import React, { useCallback, useEffect, useMemo,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logError } from 'App/services/faroService';
import { CONTACT_US_DESCRIPTION_MAX_CHARACTERS } from 'Pages/help/subPages/contactUs/constants';
import {
  FormContainer,
  InputFieldWrapper
} from 'Pages/help/subPages/contactUs/form/style';
import { useWarningState } from 'Pages/help/subPages/contactUs/hooks/useWarningState';
import WarningPrompt from 'Pages/help/subPages/contactUs/warningPrompt/WarningPrompt';
import { CONTACT_US_TEST_IDS, DESCRIPTION_TEST_ID, SUBJECT_TEST_ID } from 'Pages/help/testId';
import { Select, SelectFieldTypes, SelectOption } from 'Shared/components/design/select/Select';
import { TextArea, TextAreaTypes } from 'Shared/components/design/textArea/TextArea';
import { TextField, TextFieldTypes } from 'Shared/components/design/textField/TextField';
import { getCategories } from 'Shared/services/mct/api/mctApi';
import { userVerificationStatuses, useUserVerificationStatus } from 'Shared/services/ver/app/verificationService';

type ContactUsFormProps = {
  nameCallback         : (input: string) => void;
  emailCallback        : (input: string) => void;
  subjectCallback      : (input: string) => void;
  categoryCallback     : (input: string) => void;
  descriptionCallback  : (input: string) => void;
  emailFieldType       : TextFieldTypes;
  subjectFieldType     : TextFieldTypes;
  nameFieldType        : TextFieldTypes;
  descriptionFieldType : TextAreaTypes;
  firstLastName        : string;
  userEmail            : string;
  isUserLoggedIn       : boolean;
  selectFieldType      : SelectFieldTypes;
  defaultValues        : {[key: string]: string};
  disableSubmitCallback: (input: boolean) => void;
}

/**
 * Renders the ContactUs form
 */
function ContactUsForm({
  nameCallback,
  emailCallback,
  subjectCallback,
  categoryCallback,
  descriptionCallback,
  emailFieldType,
  subjectFieldType,
  descriptionFieldType,
  nameFieldType,
  firstLastName,
  userEmail,
  isUserLoggedIn,
  selectFieldType,
  defaultValues,
  disableSubmitCallback
}: ContactUsFormProps): JSX.Element {
  const { t }                                   = useTranslation('accountHelp');
  const [categories, setCategories]             = useState([]);
  const [active, setIsActive]                   = useState<boolean>(false);
  const [isDataSorted, setIsDataSorted]         = useState<boolean>(false);
  const [categoryOptions, setCategoryOptions]   = useState<SelectOption[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const {
    data: verificationStatusData,
  } = useUserVerificationStatus(true);

  /**
   * Check if the warning should be shown
   */
  const showWarning = useMemo(() => (
    (verificationStatusData?.verificationStatus === userVerificationStatuses.inProgress) &&
    isUserLoggedIn &&
    (selectedCategory === 'Account Verification')
  ), [verificationStatusData?.verificationStatus, isUserLoggedIn, selectedCategory]);

  /**
   * Fallback if category option cannot be fetched from backend
   */
  const CATEGORY_OPTIONS: SelectOption[] = [
    {
      label: `${t('ContactUs--categoryOptions-pleaseSelect')}`,
      value: `${t('ContactUs--categoryOptions-pleaseSelect')}`
    },{
      label: `${t('ContactUs--categoryOptions-Pulse')}`,
      value: `${t('ContactUs--categoryOptions-Pulse')}`
    },{
      label: `${t('ContactUs--categoryOptions-payPal')}`,
      value: `${t('ContactUs--categoryOptions-payPal')}`
    },{
      label: `${t('ContactUs--categoryOptions-points')}`,
      value: `${t('ContactUs--categoryOptions-points')}`
    },{
      label: `${t('ContactUs--categoryOptions-SurveysExperience')}`,
      value: `${t('ContactUs--categoryOptions-SurveysExperience')}`
    },{
      label: `${t('ContactUs--categoryOptions-other')}`,
      value: `${t('ContactUs--categoryOptions-other')}`
    }
  ];

  /**
   * Map the category options object for Select component
   */
  const mapCategories = useCallback(() => {
    const FetchedCategoryOptions: SelectOption[] = [
      {
        label: `${t('ContactUs--categoryOptions-pleaseSelect')}`,
        value: `${t('ContactUs--categoryOptions-pleaseSelect')}`
      }
    ];

    categories.forEach(function (category) {
      const categoryObject = {
        label: category,
        value: category
      };
      FetchedCategoryOptions.push(categoryObject);
    });

    setCategoryOptions(FetchedCategoryOptions);
  },[categories, t]);

  /**
   * Fetch categories from backend
   */
  useEffect(() => {
    if(!active){
      getCategories()
        .then((response) => {
          setIsActive(true);
          setCategories(response.data?.categories);
        })
        .catch(error => {
          setIsActive(true);
          logError('getCategories', error);
        });
    }
  },[active, mapCategories]);

  /**
   * Upon fetching of categories data call the mapping function
   */
  useEffect(() => {
    if (active && !isDataSorted) {
      mapCategories();
      setIsDataSorted(true);
    }
  },[active, isDataSorted, mapCategories]);

  /**
   * Render pre-filled or blank name/email fields
   */
  const renderUsernameEmailFields = (): JSX.Element => {
    if(isUserLoggedIn) {
      return(
        <>
          <InputFieldWrapper>
            <TextField
              fieldType      = {TextFieldTypes.DISABLED_NO_BOX}
              changeCallback = {nameCallback}
              label          = {t('ContactUs--inputLabel-name')}
              initialText    = {firstLastName}
            />
          </InputFieldWrapper>
          <InputFieldWrapper>
            <TextField
              fieldType      = {TextFieldTypes.DISABLED_NO_BOX}
              changeCallback = {emailCallback}
              label          = {t('ContactUs--inputLabel-email')}
              initialText    = {userEmail}
            />
          </InputFieldWrapper>
        </>
      );
    }

    return(
      <>
        <InputFieldWrapper>
          <TextField
            fieldType      = {nameFieldType}
            changeCallback = {nameCallback}
            label          = {t('ContactUs--inputLabel-name')}
          />
        </InputFieldWrapper>
        <InputFieldWrapper>
          <TextField
            fieldType      = {emailFieldType}
            changeCallback = {emailCallback}
            label          = {t('ContactUs--inputLabel-email')}
          />
        </InputFieldWrapper>
      </>
    );
  };

  /**
   * Select category
   * @param category - category to select
   */
  const selectCategory = useCallback((category: string): void => {
    categoryCallback(category);
    setSelectedCategory(category);
  }, [categoryCallback]);

  /**
   * Determine if warning prompt should be shown and if submit button should be disabled
   */
  const { setAcceptedWarning, disableInput } = useWarningState(showWarning, disableSubmitCallback);

  return (
    <FormContainer data-testid={CONTACT_US_TEST_IDS.FORM}>
      {renderUsernameEmailFields()}
      <InputFieldWrapper>
        <Select
          label          = {t('ContactUs--inputLabel-category')}
          options        = {(categoryOptions.length > 1) ? categoryOptions : CATEGORY_OPTIONS}
          disabled       = {false}
          changeCallback = {selectCategory}
          fieldType      = {selectFieldType}
          initialValue   = {defaultValues?.category}
        />
      </InputFieldWrapper>
      {showWarning && <WarningPrompt acceptedWarningCallback={setAcceptedWarning}/>}
      <InputFieldWrapper>
        <TextField
          fieldType      = {disableInput ? TextFieldTypes.DISABLED_WITH_BOX : subjectFieldType}
          changeCallback = {subjectCallback}
          label          = {t('ContactUs--inputLabel-subject')}
          initialText    = {defaultValues?.subject}
          data-testid    = {SUBJECT_TEST_ID}
        />
      </InputFieldWrapper>
      <InputFieldWrapper>
        <TextArea
          fieldType      = {disableInput ? TextAreaTypes.DISABLED_WITH_BOX : descriptionFieldType}
          changeCallback = {descriptionCallback}
          label          = {t('ContactUs--inputLabel-description')}
          maxLength      = {CONTACT_US_DESCRIPTION_MAX_CHARACTERS}
          initialText    = {defaultValues?.description}
          testId         = {DESCRIPTION_TEST_ID}
        />
      </InputFieldWrapper>
    </FormContainer>
  );
}

export default ContactUsForm;
